import { interpolateTemplate } from '../canvas/text/BaseTextObject'
import { wrapAtPositions } from '../canvas/text/fitText'
import { resolveEffectiveStyle, type ThemeRecord } from './themeVariables'
import { extractShapeAttrs } from '../canvas/styleAttrs'
import { assignIds, migrateGeometry } from '../canvas/utils'
import type { Poster } from './posterSchema'
import type { DataRecord, RuntimeObject, RuntimeText, RuntimeImage, RuntimeContainer, Box } from '../types'
import type { IObjectImpl } from '../canvas/interactions/ObjectImpl'
import type { ContainerImpl } from '../canvas/container/ContainerImpl'
import type { ImageImpl } from '../canvas/images/ImageImpl'

// ── Flatten a container-based design into an absolutely-positioned view ─────────
//
// Containers are a design-time layout aid: they arrange children as flex columns and live-measure
// them via the DOM. A user-facing poster gains nothing from that — so on entering a user context the
// live layout is measured once and collapsed into a single, flat list of absolutely-positioned
// objects (see PosterRenderer / handleToggleAppMode).
//
// Each object's absolute canvas position is its parent-offset plus its live getBBox — the same
// accumulation SelectableObject uses to place resize handles (absLeft = parentOffset.x + box.left)
// and ContainerImpl.nestedParentOffset threads down to children. A container carrying a visible
// fill/border/shape survives as a childless background shape; a purely structural container is
// dropped and its children are hoisted (their absolute offset already includes the dropped
// container's position). The walk is depth-first pre-order so a container's shape is emitted before
// its descendants and paint order is preserved.
//
// Two flavours share the DOM-measured walk:
//   • flattenDesign — for the editable user-mode builder. Objects keep their nature (fields keep
//     their templates and stay reactive to the record); only geometry is baked.
//   • bakeViewDesign — for the read-only PosterView. Everything is resolved against a fixed record:
//     fields become plain resolved text with their rendered lines baked, image sources and
//     theme-bound styles are resolved, so the result renders with no DOM measurement.
//
// hoistDesign is the third, *measurement-free* flavour — see its own header below.

type Offset = { x: number; y: number }

// A text object carrying its already-wrapped lines and the vertical content offset it rendered at, so
// PosterView reproduces the layout without measuring.
export type ViewText   = RuntimeText & { lines?: string[]; yOffset?: number }
export type ViewObject = ViewText | RuntimeImage | RuntimeContainer

interface Bakers {
	leaf:  (obj: RuntimeObject, el: IObjectImpl | undefined, left: number, top: number, box?: Box) => RuntimeObject
	shape: (obj: RuntimeContainer, el: IObjectImpl | undefined, left: number, top: number, box?: Box) => RuntimeObject
}

// Store proxies can't be structuredClone'd; JSON round-trips read straight through them (the same
// clone the mode-switch snapshot uses).
function clone<T>(v: T): T { return JSON.parse(JSON.stringify(v)) as T }

function walk(objs: RuntimeObject[], offset: Offset, elements: Record<string, IObjectImpl>, userExists: boolean, bakers: Bakers, out: RuntimeObject[]): void
{
	for (const obj of objs) {
		const el   = elements[obj.id]
		const box  = el?.getBBox(userExists)
		const absX = offset.x + (box?.pos.x ?? obj.position?.x ?? 0)
		const absY = offset.y + (box?.pos.y ?? obj.position?.y  ?? 0)
		const left = Math.round(absX)
		const top  = Math.round(absY)

		if (obj.type === 'container') {
			const c = el as ContainerImpl | undefined
			if (c && (c.getHasFill() || c.getHasBorder() || c.isCircle()))
				out.push(bakers.shape(obj as RuntimeContainer, el, left, top, box))
			walk((obj as RuntimeContainer).objects ?? [], { x: absX, y: absY }, elements, userExists, bakers, out)
		}
		else
			out.push(bakers.leaf(obj, el, left, top, box))
	}
}

// ── flattenDesign: editable user-mode layout ────────────────────────────────────

export function flattenDesign(objects: RuntimeObject[], elements: Record<string, IObjectImpl>, userExists: boolean): RuntimeObject[]
{
	const out: RuntimeObject[] = []
	walk(objects, { x: 0, y: 0 }, elements, userExists, { leaf: editableLeaf, shape: editableShape }, out)
	return out
}

// A leaf (text/image) baked at its absolute position. Plain text carries no size — it re-wraps from
// its softWrapPositions and font metrics (the clone preserves them); fields and images keep their
// measured box. The clone preserves style, content, isField, bindings and uploaded-image data, so a
// baked field still resolves its content reactively from the record.
function editableLeaf(obj: RuntimeObject, _el: IObjectImpl | undefined, left: number, top: number, box?: Box): RuntimeObject
{
	const flat = clone(obj)
	flat.position = { x: left, y: top }

	const isPlainText = obj.type === 'text' && !(obj as RuntimeText).style?.isField
	if (isPlainText)
		delete (flat as RuntimeText).size
	else if (box)
		(flat as RuntimeImage | RuntimeText).size = { width: Math.round(box.dims.width), height: Math.round(box.dims.height) }

	return flat
}

// A visible container reduced to a background shape: no children, no layout, just its style at the
// measured absolute box.
function editableShape(obj: RuntimeContainer, _el: IObjectImpl | undefined, left: number, top: number, box?: Box): RuntimeContainer
{
	const flat = clone(obj)
	flat.objects  = []
	flat.layout   = 'absolute'
	flat.position = { x: left, y: top }
	if (box)
		flat.size = { width: Math.round(box.dims.width), height: Math.round(box.dims.height) }
	return flat
}

// ── hoistDesign: structural, measurement-free flatten ───────────────────────────
//
// Collapses the nested template into the flat leaf list a user-facing poster is made of, without
// touching the DOM: containers are structure, so they can be walked on the spec alone. This is what
// makes the flatten *immediate* — a poster is flat from the moment it is copied onto a record, both in
// memory and in the DB, so no nested design is ever persisted or rendered outside design mode.
//
// It carries no layout authority: each leaf's position is only the sum of its stored offsets, which
// says nothing for a flex column (the container places its children by measured height). The real
// layout comes from the *shadow* — the nested template kept alongside, laid out live by designLayout
// against the mounted leaves' measured sizes (see designLink) and baked into these positions at save
// time (bakeLinkedPositions). Hoisted positions are therefore a seed, not the answer.
//
// A container with a visible fill/border/shape survives as a childless background shape; a purely
// structural one is dropped and its children keep the offset it contributed. Ids are preserved
// unchanged — they are the only thing linking a flat leaf back to its shadow leaf.

export function hoistDesign(objects: RuntimeObject[], theme: ThemeRecord | undefined): RuntimeObject[]
{
	const out: RuntimeObject[] = []
	hoistInto(objects, { x: 0, y: 0 }, theme, out)
	return out
}

// hoistDesign over a whole design, seeding ids/geometry on the nested tree first. The ids must be
// minted *before* the hoist: assignIds numbers by tree order, so the shadow's own pass over the same
// nested tree mints the same ids, where a pass over the flat list would not.
export function hoistPoster(design: Poster, theme: ThemeRecord | undefined): Poster
{
	const nested = clone(design)
	const objects = (nested.objects ?? []) as RuntimeObject[]
	migrateGeometry(objects)
	assignIds(objects)
	return { ...nested, objects: hoistDesign(objects, theme) as Poster['objects'] }
}

// Whether the design still carries a container with children — i.e. whether hoistDesign has work to do.
export function hasNestedObjects(objects: RuntimeObject[]): boolean
{
	return objects.some(obj => obj.type === 'container' && !!(obj as RuntimeContainer).objects?.length)
}

function hoistInto(objs: RuntimeObject[], offset: Offset, theme: ThemeRecord | undefined, out: RuntimeObject[]): void
{
	for (const obj of objs) {
		const left = offset.x + (obj.position?.x ?? 0)
		const top  = offset.y + (obj.position?.y ?? 0)

		if (obj.type === 'container') {
			const c = obj as RuntimeContainer
			if (isVisibleShape(c, theme))
				out.push(hoistedShape(c, left, top))
			hoistInto(c.objects ?? [], { x: left, y: top }, theme, out)
		}
		else
			out.push(hoistedLeaf(obj, left, top))
	}
}

// A leaf at its accumulated offset. An object carrying no position at the canvas origin is left
// position-less: that is how a full-bleed image is spelled, and inventing a {0,0} would pin it.
// Plain text keeps no size (it re-wraps from softWrapPositions and font metrics); a field keeps its
// stored width and an image its viewport.
function hoistedLeaf(obj: RuntimeObject, left: number, top: number): RuntimeObject
{
	const flat = clone(obj)

	if (obj.position || left !== 0 || top !== 0)
		flat.position = { x: Math.round(left), y: Math.round(top) }

	if (obj.type === 'text' && !(obj as RuntimeText).style?.isField)
		delete (flat as RuntimeText).size

	return flat
}

// A visible container reduced to a background shape at its accumulated offset. Its stored size is
// kept as the seed; the shadow's layout supplies the size it actually draws at (a container grows to
// fit its children, so the stored one is only a minimum).
function hoistedShape(obj: RuntimeContainer, left: number, top: number): RuntimeContainer
{
	const flat = clone(obj)
	flat.objects  = []
	flat.layout   = 'absolute'
	flat.position = { x: Math.round(left), y: Math.round(top) }
	return flat
}

// The spec-only reading of ContainerImpl's getHasFill/getHasBorder/isCircle: a container draws
// something of its own when its effective style resolves to a fill, a stroke, or a circle.
function isVisibleShape(c: RuntimeContainer, theme: ThemeRecord | undefined): boolean
{
	const style = resolveEffectiveStyle(c, theme)
	const attrs = extractShapeAttrs(style)
	const filled = !!attrs.fill && attrs.fill !== 'none' && attrs.fill !== 'transparent'
	return filled || !!attrs.stroke || String(style?.borderRadius) === '50%'
}

// ── bakeViewDesign: fully-resolved, measurement-free view ────────────────────────

export function bakeViewDesign(objects: RuntimeObject[], elements: Record<string, IObjectImpl>, record: DataRecord, theme: ThemeRecord | undefined, userExists: boolean): ViewObject[]
{
	const out: RuntimeObject[] = []
	walk(objects, { x: 0, y: 0 }, elements, userExists, {
		leaf:  (obj, el, left, top, box) => viewLeaf(obj, el, left, top, box, record, theme),
		shape: (obj, _el, left, top, box) => viewShape(obj, left, top, box, theme),
	}, out)
	return out as ViewObject[]
}

function viewLeaf(obj: RuntimeObject, el: IObjectImpl | undefined, left: number, top: number, box: Box | undefined, record: DataRecord, theme: ThemeRecord | undefined): RuntimeObject
{
	const flat = clone(obj)
	flat.position = { x: left, y: top }
	if (box)
		(flat as RuntimeImage | RuntimeText).size = { width: Math.round(box.dims.width), height: Math.round(box.dims.height) }

	if (obj.type === 'text') {
		const text = obj as RuntimeText
		const resolved = text.style?.isField ? interpolateTemplate(text.content ?? '', record, true) : (text.content ?? '')
		const view = flat as ViewText
		view.content = resolved
		view.style   = resolveEffectiveStyle(obj, theme)
		view.lines   = renderedLines(el) ?? (text.softWrapPositions?.length ? wrapAtPositions(resolved, text.softWrapPositions) : resolved.split('\n'))
		view.yOffset = el?.getContentOffsetY?.() ?? 0
		delete view.isField
		if (view.style) delete (view.style as { isField?: boolean }).isField
		return view
	}

	if (obj.type === 'image') {
		const img = flat as RuntimeImage
		img.src = (el as ImageImpl | undefined)?.effectiveSrc?.() ?? img.src ?? null
		img.style = resolveEffectiveStyle(obj, theme)
		delete img.isField
		delete img.field
		return img
	}

	return flat
}

function viewShape(obj: RuntimeContainer, left: number, top: number, box: Box | undefined, theme: ThemeRecord | undefined): RuntimeContainer
{
	const flat = clone(obj)
	flat.objects  = []
	flat.layout   = 'absolute'
	flat.position = { x: left, y: top }
	flat.style    = resolveEffectiveStyle(obj, theme)
	if (box)
		flat.size = { width: Math.round(box.dims.width), height: Math.round(box.dims.height) }
	return flat
}

// The lines a text object actually rendered, read straight off its <text> element's <tspan>s — so the
// baked view reproduces the live wrap exactly, with no re-measurement. Undefined when the element
// isn't mounted (the caller falls back to softWrapPositions / explicit newlines).
function renderedLines(el: IObjectImpl | undefined): string[] | undefined
{
	const textEl = el?.getTextEl?.()
	if (!textEl) return undefined
	const tspans = textEl.querySelectorAll('tspan')
	if (tspans.length) return Array.from(tspans, t => t.textContent ?? '')
	return textEl.textContent ? [textEl.textContent] : undefined
}
